import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { CustomExpander } from '../../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { observer } from 'mobx-react-lite';

export interface SalaryDateFiltersProps {
  onSearch: (filters: {
    dateRange: [Date | null, Date | null];
    includeHistoricalMissingPayments: boolean;
    branchIds: number[];
  }) => void;
  loading?: boolean;
}

export const SalaryDateFilters: React.FC<SalaryDateFiltersProps> = observer(
  ({ onSearch, loading = false }) => {
    // Calculate default date range
    const getDefaultDateRange = (): [Date, Date] => {
      const now = new Date();

      // Start date: first day of current month
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

      // End date (inclusive): last day of current month
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      return [startDate, endDate];
    };

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
      getDefaultDateRange(),
    );
    const [
      includeHistoricalMissingPayments,
      setIncludeHistoricalMissingPayments,
    ] = useState<boolean>(false);

    const branches = globalSettingsBranches.branches ?? [];

    const [selectedBranchId, setSelectedBranchId] = useState<number | null>(
      null,
    );
    const didInitBranchSelectionRef = useRef(false);

    const firstValidBranchId = () => {
      const id = branches.find((b) => Number.isInteger(b.id) && b.id >= 1)?.id;
      return id ?? null;
    };

    useEffect(() => {
      if (didInitBranchSelectionRef.current) {
        return;
      }
      if (branches.length === 0) {
        return;
      }
      setSelectedBranchId(firstValidBranchId());
      didInitBranchSelectionRef.current = true;
    }, [branches.length]);

    const handleClearAllFilters = () => {
      setDateRange(getDefaultDateRange());
      setIncludeHistoricalMissingPayments(false);
      setSelectedBranchId(firstValidBranchId());
    };

    /** Single-branch selection: choose another branch replaces; click again clears. */
    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchId((prev) => {
        if (prev === branchId) {
          return null;
        }
        return branchId;
      });
    };

    const handleSearch = () => {
      // Validate that both dates are provided
      if (!dateRange[0] || !dateRange[1]) {
        alert('יש לבחור תאריך התחלה וסיום');
        return;
      }

      // Validate that start date is before end date
      if (dateRange[0] > dateRange[1]) {
        alert('תאריך ההתחלה חייב להיות לפני תאריך הסיום');
        return;
      }

      if (selectedBranchId == null || selectedBranchId < 1) {
        alert('יש לבחור שלוחה');
        return;
      }

      onSearch({
        dateRange,
        includeHistoricalMissingPayments,
        branchIds: [selectedBranchId],
      });
    };

    const styles = {
      container: {
        width: '250px',
        maxWidth: '250px',
        minWidth: '250px',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      content: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      dateRangeContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        direction: 'rtl' as const,
      },
      checkboxLabel: {
        fontSize: '0.95rem',
        color: '#333',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
      searchButtonDisabled: {
        width: '100%',
        backgroundColor: '#9CA3AF',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'not-allowed',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            tooltip={'נקה הכל'}
            severity="secondary"
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={branches.filter((b) => Number.isInteger(b.id) && b.id >= 1)}
            selectedItems={selectedBranchId != null ? [selectedBranchId] : []}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
            allowSelectAll={false}
            allowClear
          />

          <CustomExpander>
            <div>טווח תאריכים</div>
            <div style={styles.dateRangeContainer}>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>התחלה:</span>
                <Calendar
                  value={dateRange[0]}
                  onChange={(e) =>
                    setDateRange([e.value as Date, dateRange[1]])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>סיום (כולל):</span>
                <Calendar
                  value={dateRange[1]}
                  onChange={(e) =>
                    setDateRange([dateRange[0], e.value as Date])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  minDate={dateRange[0] || undefined}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>אפשרויות נוספות</div>
            <div style={styles.checkboxContainer}>
              <Checkbox
                checked={includeHistoricalMissingPayments}
                onChange={(e) =>
                  setIncludeHistoricalMissingPayments(e.checked || false)
                }
              />
              <span style={styles.checkboxLabel}>
                כלול תשלומים חסרים היסטוריים
              </span>
            </div>
          </CustomExpander>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label={loading ? 'מחפש...' : 'חפש'}
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
            disabled={loading}
            style={loading ? styles.searchButtonDisabled : styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
